import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  FormLabel,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Textarea,
  Tooltip,
  CircularProgress,
  Breadcrumbs,
  Select,
  Option,
} from '@mui/joy';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  KeyboardArrowRight,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { Tooltip as MuiTooltip } from '@mui/material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import {
  getShipmentTypes,
  getShipmentType,
  createShipmentType,
  updateShipmentType,
  deleteShipmentType,
} from '../../main/API/shipmentType';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import DeleteAlert from '../../main/widgets/DeleteAlert';
import { getPaginatedMeta } from '../../main/utils/pagination';
import { ParamsTable, SHIPMENT_STATUS_PARAMS } from '../constants/contentSidParams';
import {
  DEFAULT_CBM_UNIT_LABELS,
  DEFAULT_KG_UNIT_LABELS,
  hasCbmDefaultUnitLabels,
  hasKgDefaultUnitLabels,
} from '../../main/utils/formatInvoiceQty';

const BILLING_METRIC_KG = 'kg';
const BILLING_METRIC_CBM = 'cbm';
const INVOICE_UNIT_LABEL_MAX = 20;

const EMPTY_BILLING_FIELDS = {
  billing_metric: BILLING_METRIC_KG,
  ...DEFAULT_KG_UNIT_LABELS,
};

const SHIPMENT_TYPES_SCREEN_LIST_PERMISSIONS = [PERMISSIONS.SHIPMENT_TYPES_LIST];
/** POST list URL: list + create (backend gates both). */
const SHIPMENT_TYPES_SCREEN_CREATE_PERMISSIONS = [
  PERMISSIONS.SHIPMENT_TYPES_LIST,
  PERMISSIONS.SHIPMENT_TYPES_CREATE,
];
/** Detail PUT/PATCH: view + update. */
const SHIPMENT_TYPES_SCREEN_UPDATE_PERMISSIONS = [
  PERMISSIONS.SHIPMENT_TYPES_VIEW,
  PERMISSIONS.SHIPMENT_TYPES_UPDATE,
];
/** Soft-delete: view + delete. */
const SHIPMENT_TYPES_SCREEN_DELETE_PERMISSIONS = [
  PERMISSIONS.SHIPMENT_TYPES_VIEW,
  PERMISSIONS.SHIPMENT_TYPES_DELETE,
];

/** Status WhatsApp template SIDs — optional; empty falls back to matching Settings key. */
const STATUS_CONTENT_SID_FIELDS = [
  'SHIPMENT_SHIPPING_SOON_CONTENT_SID',
  'SHIPMENT_SHIPPED_CONTENT_SID',
  'SHIPMENT_ARRIVED_CONTENT_SID',
  'SHIPMENT_OUT_FOR_DELIVERY_CONTENT_SID',
  'SHIPMENT_DELIVERED_CONTENT_SID',
];

const ALL_CONTENT_SID_FIELDS = [...STATUS_CONTENT_SID_FIELDS, 'STORE_CONTENT_SID'];

const EMPTY_CONTENT_SIDS = Object.fromEntries(ALL_CONTENT_SID_FIELDS.map((key) => [key, '']));

/** Trimmed non-empty string, otherwise null (clear override → Settings fallback). */
const normalizeContentSid = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
};

/** Same display style as WhatsAppMessagesScreen / Twilio settings labels. */
const formatSettingName = (name) => {
  if (!name) return '';

  const acronyms = ['API', 'SID', 'URL', 'HMAC', 'SMS', 'ID', 'UID', 'UUID'];

  const parts = name.split('_').map((part) => {
    const upperPart = part.toUpperCase();

    if (acronyms.includes(upperPart)) {
      return upperPart;
    }

    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  });

  return parts.join(' ');
};

const buildShipmentTypePayload = (formData) => {
  const metric =
    formData.billing_metric === BILLING_METRIC_CBM ? BILLING_METRIC_CBM : BILLING_METRIC_KG;
  const payload = {
    name: formData.name || '',
    description: formData.description || '',
    billing_metric: metric,
    invoice_unit_label_en: String(formData.invoice_unit_label_en || '').trim().slice(0, INVOICE_UNIT_LABEL_MAX),
    invoice_unit_label_ar: String(formData.invoice_unit_label_ar || '').trim().slice(0, INVOICE_UNIT_LABEL_MAX),
  };
  ALL_CONTENT_SID_FIELDS.forEach((key) => {
    payload[key] = normalizeContentSid(formData[key]);
  });
  return payload;
};

/** Map API row → form billing fields (defaults when missing). */
const billingFieldsFromShipmentType = (shipmentType) => {
  const metric =
    shipmentType?.billing_metric === BILLING_METRIC_CBM ? BILLING_METRIC_CBM : BILLING_METRIC_KG;
  const defaults = metric === BILLING_METRIC_CBM ? DEFAULT_CBM_UNIT_LABELS : DEFAULT_KG_UNIT_LABELS;
  return {
    billing_metric: metric,
    invoice_unit_label_en:
      shipmentType?.invoice_unit_label_en || defaults.invoice_unit_label_en,
    invoice_unit_label_ar:
      shipmentType?.invoice_unit_label_ar || defaults.invoice_unit_label_ar,
  };
};

export default function ShipmentTypesScreen() {
  const { t } = useTranslation('ui');
  const { hasAnyPermission, hasAllPermissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [shipmentTypes, setShipmentTypes] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    search: '',
  });
  const [openModal, setOpenModal] = useState(false);
  const [editingShipmentType, setEditingShipmentType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ...EMPTY_BILLING_FIELDS,
    ...EMPTY_CONTENT_SIDS,
  });
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  
  // Delete alert states
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedShipmentTypeId, setSelectedShipmentTypeId] = useState(null);

  const loadShipmentTypes = useCallback(async () => {
    if (!hasAnyPermission(SHIPMENT_TYPES_SCREEN_LIST_PERMISSIONS)) {
      setShipmentTypes([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        ...filters,
      };
      
      const response = await getShipmentTypes(params);
      if (response && response.data) {
        const meta = getPaginatedMeta(response.data, paginationModel.pageSize);
        setData(meta);
        setShipmentTypes(meta.results);
      } else {
        setShipmentTypes([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load shipment types:', error);
      setResponseMessage(error.message || t('settingsPages.shipmentTypes.loadFailed'));
      setFailedNotification(true);
      setShipmentTypes([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasAnyPermission, paginationModel.page, paginationModel.pageSize, filters, t]);

  useEffect(() => {
    loadShipmentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleOpenModal = useCallback((shipmentType = null) => {
    if (shipmentType) {
      setEditingShipmentType(shipmentType);
      const sidValues = Object.fromEntries(
        ALL_CONTENT_SID_FIELDS.map((key) => [key, shipmentType[key] || ''])
      );
      setFormData({
        name: shipmentType.name || '',
        description: shipmentType.description || '',
        ...billingFieldsFromShipmentType(shipmentType),
        ...sidValues,
      });
    } else {
      setEditingShipmentType(null);
      setFormData({
        name: '',
        description: '',
        ...EMPTY_BILLING_FIELDS,
        ...EMPTY_CONTENT_SIDS,
      });
    }
    setOpenModal(true);
  }, []);

  const handleBillingMetricChange = useCallback((_, value) => {
    const nextMetric = value === BILLING_METRIC_CBM ? BILLING_METRIC_CBM : BILLING_METRIC_KG;
    setFormData((prev) => {
      const next = { ...prev, billing_metric: nextMetric };
      // UX: auto-fill unit labels when still at the other metric's defaults
      if (nextMetric === BILLING_METRIC_CBM && hasKgDefaultUnitLabels(prev)) {
        return { ...next, ...DEFAULT_CBM_UNIT_LABELS };
      }
      if (nextMetric === BILLING_METRIC_KG && hasCbmDefaultUnitLabels(prev)) {
        return { ...next, ...DEFAULT_KG_UNIT_LABELS };
      }
      return next;
    });
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingShipmentType(null);
  };

  const handleSave = async () => {
    const requiredSavePermissions = editingShipmentType ? SHIPMENT_TYPES_SCREEN_UPDATE_PERMISSIONS : SHIPMENT_TYPES_SCREEN_CREATE_PERMISSIONS;
    if (!hasAllPermissions(requiredSavePermissions)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    try {
      setLoading(true);

      // Content SIDs are optional: empty/whitespace → null (Settings fallback for status SIDs)
      const payload = buildShipmentTypePayload(formData);

      if (editingShipmentType) {
        await updateShipmentType(editingShipmentType.id, payload);
        setResponseMessage(t('settingsPages.shipmentTypes.updatedSuccess'));
      } else {
        await createShipmentType(payload);
        setResponseMessage(t('settingsPages.shipmentTypes.createdSuccess'));
      }
      setSuccessNotification(true);
      handleCloseModal();
      loadShipmentTypes();
    } catch (error) {
      console.error('Failed to save shipment type:', error);
      const errorMessage = error.response?.data 
        ? (typeof error.response.data === 'string' 
            ? error.response.data 
            : Object.values(error.response.data).flat().join(', '))
        : (error.message || t('settingsPages.shipmentTypes.saveFailed'));
      setResponseMessage(errorMessage);
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = useCallback((id) => {
    setSelectedShipmentTypeId(id);
    setDeleteAlert(true);
  }, []);

  const confirmDelete = async () => {
    if (!selectedShipmentTypeId) return;
    if (!hasAllPermissions(SHIPMENT_TYPES_SCREEN_DELETE_PERMISSIONS)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    
    try {
      setDeleteLoading(true);
      await deleteShipmentType(selectedShipmentTypeId);
      setResponseMessage(t('settingsPages.shipmentTypes.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteAlert(false);
      setSelectedShipmentTypeId(null);
      loadShipmentTypes();
    } catch (error) {
      console.error('Failed to delete shipment type:', error);
      setResponseMessage(error.message || t('settingsPages.shipmentTypes.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadShipmentTypes();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return t('actions.na');
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  }, [t]);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('settingsPages.shipmentTypes.colId'), flex: 1, minWidth: 80 },
      { field: 'name', headerName: t('settingsPages.shipmentTypes.colName'), flex: 2, minWidth: 200 },
      {
        field: 'description',
        headerName: t('actions.description'),
        flex: 2,
        minWidth: 200,
        renderCell: (params) => (
          <Typography sx={{ fontSize: '14px' }}>
            {params.row.description || t('actions.na')}
          </Typography>
        ),
      },
      {
        field: 'billing_metric',
        headerName: t('settingsPages.shipmentTypes.colBillingMetric'),
        flex: 1.2,
        minWidth: 120,
        renderCell: (params) => {
          const metric = params.row.billing_metric === BILLING_METRIC_CBM
            ? BILLING_METRIC_CBM
            : BILLING_METRIC_KG;
          return (
            <Typography sx={{ fontSize: '14px' }}>
              {metric === BILLING_METRIC_CBM
                ? t('settingsPages.shipmentTypes.billingMetricCbmShort')
                : t('settingsPages.shipmentTypes.billingMetricKgShort')}
            </Typography>
          );
        },
      },
      {
        field: 'invoice_unit',
        headerName: t('settingsPages.shipmentTypes.colInvoiceUnit'),
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => {
          const metric = params.row.billing_metric === BILLING_METRIC_CBM
            ? BILLING_METRIC_CBM
            : BILLING_METRIC_KG;
          const defaults = metric === BILLING_METRIC_CBM
            ? DEFAULT_CBM_UNIT_LABELS
            : DEFAULT_KG_UNIT_LABELS;
          const en = params.row.invoice_unit_label_en || defaults.invoice_unit_label_en;
          const ar = params.row.invoice_unit_label_ar || defaults.invoice_unit_label_ar;
          return (
            <Typography sx={{ fontSize: '14px' }}>
              {en} / {ar}
            </Typography>
          );
        },
      },
      {
        field: 'content_sids',
        headerName: t('settingsPages.shipmentTypes.colContentSids'),
        flex: 2.5,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => {
          const statusOverrides = STATUS_CONTENT_SID_FIELDS.filter(
            (key) => normalizeContentSid(params.row[key])
          ).length;
          const storeSid = normalizeContentSid(params.row.STORE_CONTENT_SID);
          return (
            <Box sx={{ py: 0.5 }}>
              <Typography sx={{ fontSize: '13px', color: '#555' }}>
                {statusOverrides === 0
                  ? t('settingsPages.shipmentTypes.allStatusDefaultSettings')
                  : t('settingsPages.shipmentTypes.statusOverridesCount', {
                      count: statusOverrides,
                      total: STATUS_CONTENT_SID_FIELDS.length,
                    })}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#888' }}>
                {t('settingsPages.shipmentTypes.storeSidShort')}:{' '}
                {storeSid || t('settingsPages.shipmentTypes.defaultSettings')}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'created_at',
        headerName: t('trackingEvent.colCreatedAt'),
        flex: 1.5,
        minWidth: 150,
        renderCell: (params) => formatDate(params.row.created_at),
      },
      {
        field: 'updated_at',
        headerName: t('trackingEvent.colModifiedAt'),
        flex: 1.5,
        minWidth: 150,
        renderCell: (params) => formatDate(params.row.updated_at),
      },
      {
        field: 'actions',
        headerName: t('settingsPages.shipmentTypes.colActions'),
        flex: 2,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {hasAllPermissions(SHIPMENT_TYPES_SCREEN_UPDATE_PERMISSIONS) && (
              <Tooltip title={t('settingsPages.shipmentTypes.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModal(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasAllPermissions(SHIPMENT_TYPES_SCREEN_DELETE_PERMISSIONS) && (
              <Tooltip title={t('settingsPages.shipmentTypes.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(params.row.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, hasAllPermissions, formatDate, handleOpenModal, handleDelete]
  );

  const SHIPMENT_STATUS_SID_HELP = (
    <ParamsTable
      params={SHIPMENT_STATUS_PARAMS}
      subtitle={t('settingsPages.shipmentTypes.templateHint')}
    />
  );
  const STORE_SID_HELP = (
    <span style={{ fontSize: '0.8125rem', display: 'block' }}>
      {t('settingsPages.shipmentTypes.storeContentSidHelp')}
      <br />
      {t('settingsPages.twilio.helpNoParams')}
    </span>
  );
  const tooltipProps = { placement: 'top', arrow: true, componentsProps: { tooltip: { sx: { maxWidth: 440, maxHeight: 380 } } } };

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/settings">
                  {t('settingsPages.shipmentTypes.breadcrumbSettings')}
                </Link>
                <Typography level="body-md">{t('settingsPages.shipmentTypes.breadcrumbCurrent')}</Typography>
              </Breadcrumbs>
              <Typography textAlign={"start"} level="h1">{t('settingsPages.shipmentTypes.pageTitle')}</Typography>
            </Box>

            <PermissionGate permissions={SHIPMENT_TYPES_SCREEN_LIST_PERMISSIONS}>
              {/* Filters */}
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Input
                    placeholder={t('settingsPages.shipmentTypes.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Button type="submit" loading={loading} startDecorator={<SearchIcon />}>
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Actions icons */}
              <Grid mt={2} container spacing={2} justifyContent="flex-end">
                <Grid
                  container
                  spacing={1}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start'
                  }}
                >
                  <PermissionGate permissions={SHIPMENT_TYPES_SCREEN_CREATE_PERMISSIONS} requireAll>
                    <Grid item>
                      <Tooltip title={t('settingsPages.shipmentTypes.btnAdd')}>
                        <IconButton
                          onClick={() => handleOpenModal()}
                          color="primary"
                          size="large"
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </PermissionGate>
                </Grid>
              </Grid>

              {/* Table */}
              <Box>
                <DataGrid
                  rows={shipmentTypes || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? data.count : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Create/Edit Modal */}
            <Modal open={openModal} onClose={handleCloseModal}>
              <ModalDialog sx={{ maxWidth: 800, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                <ModalClose />
                <Typography level="h4" sx={{ mb: 2 }}>
                  {editingShipmentType
                    ? t('settingsPages.shipmentTypes.modalEditTitle')
                    : t('settingsPages.shipmentTypes.modalCreateTitle')}
                </Typography>
                <Stack spacing={2}>
                  <FormLabel>{t('settingsPages.shipmentTypes.labelName')}</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('settingsPages.shipmentTypes.nameOptional')}
                  />
                  
                  <FormLabel>{t('actions.description')}</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t('actions.description')}
                    minRows={3}
                  />

                  <Box sx={{ mt: 1 }}>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                      {t('settingsPages.shipmentTypes.billingSectionTitle')}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 1.5 }}>
                      {t('settingsPages.shipmentTypes.billingSectionHelp')}
                    </Typography>
                  </Box>

                  <FormLabel>{t('settingsPages.shipmentTypes.labelBillingMetric')}</FormLabel>
                  <Select
                    value={formData.billing_metric || BILLING_METRIC_KG}
                    onChange={handleBillingMetricChange}
                  >
                    <Option value={BILLING_METRIC_KG}>
                      {t('settingsPages.shipmentTypes.billingMetricKg')}
                    </Option>
                    <Option value={BILLING_METRIC_CBM}>
                      {t('settingsPages.shipmentTypes.billingMetricCbm')}
                    </Option>
                  </Select>

                  <FormLabel>{t('settingsPages.shipmentTypes.labelInvoiceUnitEn')}</FormLabel>
                  <Input
                    value={formData.invoice_unit_label_en}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        invoice_unit_label_en: e.target.value.slice(0, INVOICE_UNIT_LABEL_MAX),
                      }))
                    }
                    placeholder={
                      formData.billing_metric === BILLING_METRIC_CBM
                        ? DEFAULT_CBM_UNIT_LABELS.invoice_unit_label_en
                        : DEFAULT_KG_UNIT_LABELS.invoice_unit_label_en
                    }
                    slotProps={{ input: { maxLength: INVOICE_UNIT_LABEL_MAX } }}
                  />

                  <FormLabel>{t('settingsPages.shipmentTypes.labelInvoiceUnitAr')}</FormLabel>
                  <Input
                    value={formData.invoice_unit_label_ar}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        invoice_unit_label_ar: e.target.value.slice(0, INVOICE_UNIT_LABEL_MAX),
                      }))
                    }
                    placeholder={
                      formData.billing_metric === BILLING_METRIC_CBM
                        ? DEFAULT_CBM_UNIT_LABELS.invoice_unit_label_ar
                        : DEFAULT_KG_UNIT_LABELS.invoice_unit_label_ar
                    }
                    slotProps={{ input: { maxLength: INVOICE_UNIT_LABEL_MAX, dir: 'rtl' } }}
                  />

                  <Box sx={{ mt: 1 }}>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                      {t('settingsPages.shipmentTypes.contentSidsSectionTitle')}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 1.5 }}>
                      {t('settingsPages.shipmentTypes.contentSidsSectionHelp')}
                    </Typography>
                  </Box>

                  {STATUS_CONTENT_SID_FIELDS.map((fieldKey) => (
                    <Box key={fieldKey}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FormLabel sx={{ mb: 0 }}>
                          {formatSettingName(fieldKey)} {t('settingsPages.shipmentTypes.optionalSuffix')}
                        </FormLabel>
                        <MuiTooltip title={SHIPMENT_STATUS_SID_HELP} {...tooltipProps}>
                          <InfoIcon sx={{ fontSize: 16, cursor: 'help', color: '#6B7280' }} />
                        </MuiTooltip>
                      </Box>
                      <Input
                        value={formData[fieldKey]}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [fieldKey]: e.target.value }))}
                        placeholder={t('settingsPages.shipmentTypes.contentSidPlaceholderDefaults')}
                        sx={{ mt: 0.5 }}
                      />
                      {!normalizeContentSid(formData[fieldKey]) && (
                        <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                          {t('settingsPages.shipmentTypes.usesSettingsKey', {
                            key: formatSettingName(fieldKey),
                          })}
                        </Typography>
                      )}
                    </Box>
                  ))}

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FormLabel sx={{ mb: 0 }}>
                        {formatSettingName('STORE_CONTENT_SID')}{' '}
                        {t('settingsPages.shipmentTypes.optionalSuffix')}
                      </FormLabel>
                      <MuiTooltip title={STORE_SID_HELP} {...tooltipProps}>
                        <InfoIcon sx={{ fontSize: 16, cursor: 'help', color: '#6B7280' }} />
                      </MuiTooltip>
                    </Box>
                    <Input
                      value={formData.STORE_CONTENT_SID}
                      onChange={(e) => setFormData((prev) => ({ ...prev, STORE_CONTENT_SID: e.target.value }))}
                      placeholder={t('settingsPages.shipmentTypes.contentSidPlaceholderDefaults')}
                      sx={{ mt: 0.5 }}
                    />
                    <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                      {normalizeContentSid(formData.STORE_CONTENT_SID)
                        ? t('settingsPages.shipmentTypes.storeContentSidHelp')
                        : t('settingsPages.shipmentTypes.usesSettingsKey', {
                            key: formatSettingName('STORE_CONTENT_SID'),
                          })}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={handleCloseModal}>
                      {t('actions.cancel')}
                    </Button>
                    <PermissionGate
                      permissions={editingShipmentType ? SHIPMENT_TYPES_SCREEN_UPDATE_PERMISSIONS : SHIPMENT_TYPES_SCREEN_CREATE_PERMISSIONS}
                      requireAll
                    >
                      <Button
                        onClick={handleSave}
                        disabled={loading}
                        startDecorator={loading ? <CircularProgress size="sm" /> : <SaveIcon />}
                      >
                        {loading ? t('settingsPages.common.saving') : t('actions.save')}
                      </Button>
                    </PermissionGate>
                  </Box>
                </Stack>
              </ModalDialog>
            </Modal>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <DeleteAlert
              label={t('widgets.shipmentType')}
              open={deleteAlert}
              onDelete={confirmDelete}
              onClose={() => {
                setDeleteAlert(false);
                setSelectedShipmentTypeId(null);
              }}
              loading={deleteLoading}
            />
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

